/** Course adapter: only course reads/context, with the shared conversation state machine. */
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api-client';
import { ensureOk } from '../lib/api-error';
import { ASK_ONCE, chatSessionUserId, pickLang, type ChatMsg } from '../lib/chat-sync';
import { useAuth } from '../store/auth';
import type { CourseChatPersona, CourseChatPreview, CourseChatTarget } from '../types/course';
import { useChatSession } from './useChatSession';

export type CourseChatEntry = { kind: 'target'; target: CourseChatTarget } | { kind: 'thread'; threadId: string };
interface CourseThread {
  id: string; surface: 'course'; target: CourseChatTarget; persona: CourseChatPersona;
  messages: ChatMsg[];
}
export function useCourseChat(entry: CourseChatEntry) {
  const jwt = useAuth((s) => s.jwt);
  const knownUserId = useAuth((s) => s.user?.id);
  const userId = chatSessionUserId(jwt, knownUserId);
  const { i18n, t } = useTranslation('course');
  const threadId = entry.kind === 'thread' ? entry.threadId : null;
  const thread = useQuery({
    queryKey: ['course-chat-thread', threadId], enabled: !!jwt && !!threadId, ...ASK_ONCE, gcTime: 0,
    queryFn: async () => (await (await ensureOk(await apiFetch(`/api/v1/course/chat/threads/${encodeURIComponent(threadId!)}`, jwt!))).json()) as CourseThread,
  });
  const target = entry.kind === 'target' ? entry.target : thread.data?.target;
  const key = target ? JSON.stringify(target) : `thread:${threadId}`;
  const preview = useQuery({
    queryKey: ['course-chat-preview', key], enabled: !!jwt && !!target, ...ASK_ONCE, gcTime: 0,
    queryFn: async () => {
      const qs = new URLSearchParams(target as Record<string, string>);
      return (await (await ensureOk(await apiFetch(`/api/v1/course/chat/target?${qs}`, jwt!))).json()) as CourseChatPreview;
    },
  });
  const knownId = threadId ?? preview.data?.conversationId ?? null;
  const existing = useQuery({
    queryKey: ['course-chat-thread', knownId], enabled: !!jwt && !!knownId && !threadId, ...ASK_ONCE, gcTime: 0,
    queryFn: async () => (await (await ensureOk(await apiFetch(`/api/v1/course/chat/threads/${encodeURIComponent(knownId!)}`, jwt!))).json()) as CourseThread,
  });
  const data = threadId ? thread.data : existing.data;
  const query = thread.isError ? thread : preview.isError ? preview : existing.isError ? existing : preview;
  const ready = !!preview.data && (!knownId || !!data);
  const session = useChatSession({
    targetKey: key, base: ready ? data?.messages ?? [] : null,
    loadedHeader: null, knownChartId: null, chartId: null, threadId: knownId,
    isNew: entry.kind === 'target', query, jwt, lang: pickLang(i18n.language),
    course: {
      preview: preview.data ?? null,
      // Scoped to the login and target. No JWT or answer is put in a URL or cache key sent to the API.
      storageKey: userId ? `course-chat-pending:${userId}:${key}` : undefined,
      refreshPreview: () => { void preview.refetch(); },
      loadStored: async (id, signal) => {
        try {
          const res = await ensureOk(await apiFetch(`/api/v1/course/chat/threads/${encodeURIComponent(id)}`, jwt!, { signal }));
          return ((await res.json()) as CourseThread).messages;
        } catch { return null; }
      },
    },
  });
  const opened = useRef(false);
  // Defer to a microtask so React StrictMode's probe cannot abort the only auto-open.
  // A saved pending request is always recovered by reading, including on history URLs.
  useEffect(() => {
    if (!ready || opened.current) return;
    let active = true;
    queueMicrotask(() => {
      if (!active || opened.current) return;
      if (session.resume()) { opened.current = true; return; }
      if (entry.kind === 'thread') { opened.current = true; return; }
      if (!preview.data?.enabled) return;
      opened.current = true;
      session.open();
    });
    return () => { active = false; };
  }, [ready, entry.kind, preview.data?.enabled, session]);
  const greeting = preview.data?.greeting;
  const answer = greeting?.answerPreview ?? null;
  const kind = target?.kind;
  const greetingKey = preview.data?.targetMissing ? 'missing'
    : kind === 'quiz' ? 'quiz' : kind === 'exam_item' ? 'exam'
    : answer !== null ? 'answer' : 'exercise';
  const quickKeys = preview.data?.targetMissing ? ['returnLesson', 'hint', 'start']
    : kind === 'quiz' ? ['explainQuestion', 'quizHint', ...(answer !== null ? ['explainOption'] : ['start'])]
    : kind === 'exam_item' ? ['findError', 'explainKey', 'returnLesson']
    : answer !== null ? ['review', 'weakPoint', 'solution'] : ['explainTask', 'hint', 'start'];
  // This single local bubble is display-only: never persisted, sent to the model or billed.
  const messages: ChatMsg[] = ready ? [{ role: 'assistant', content: t(`chat.greeting.${greetingKey}`, { title: greeting?.title ?? preview.data?.persona.itemTitle, answer }) }, ...session.messages] : session.messages;
  return { ...session, messages,
    quickReplies: session.messages.some((m) => m.role === 'user') ? [] : quickKeys.map((key) => t(`chat.quick.${key}`)),
    draftStorageKey: userId && target ? `course-chat-draft:${userId}:${key}` : undefined,
    preview: preview.data ?? null, loadCode: query.error instanceof Error && 'code' in query.error ? String(query.error.code) : null };
}
